import { useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Navbar, Nav, NavDropdown, Button, Modal, Row, Col, Form } from "react-bootstrap";
import { FaBars, FaUserCircle } from "react-icons/fa";
import { GiHamburgerMenu } from "react-icons/gi";
import {
  Menu,
  MenuItem,
  ProSidebar,
  SidebarContent,
  SubMenu,
} from "react-pro-sidebar";
import Logo from "./../../assets/img/logo-horizontal.png";
import useAuth from "../../contexts/auth";
import useWindowSize from "../../hooks/useWindowSize";
import useCategories from "../../hooks/useCategories";
import "./style.scss";

export default function NavBar() {
  const { user, logout } = useAuth();
  const { width } = useWindowSize();
  const history = useHistory();
  const location = useLocation();
  const categories = useCategories();
  const [toggled, setToggled] = useState(false);
  const [lastCategory, setLastCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [headerSearch, setHeaderSearch] = useState("");

  const closeSidebar = () => setToggled(false);

  const isMarketplaceRoute =
    location.pathname === "/" ||
    location.pathname.startsWith("/categoria/") ||
    location.pathname.startsWith("/subCategory/") ||
    location.pathname.startsWith("/produto/") ||
    location.pathname.startsWith("/giftvoucher/") ||
    location.pathname.startsWith("/loja/");
  // Catalog bar (Departamentos azul) substituída pelas Categorias estilo Indica no Marketplace
  const showMarketplaceCatalogBar = false;
  const isGruposRoute = location.pathname === "/grupos/" || location.pathname === "/grupos";

  const normalizeCategoryName = (value = "") =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const tvExcludedPattern = /audio\s*e\s*video/;
  const tvStrongPattern = /\bsmart\s*tv\b|\bsmart\s*tvs\b|\btelevisor\b|\btelevisores\b|\btelevis/;
  const tvLoosePattern = /\btv\b|\btvs\b/;

  const isTvCategory = (category) => {
    const normalized = normalizeCategoryName(category?.name);
    if (!normalized || tvExcludedPattern.test(normalized)) return false;
    return tvStrongPattern.test(normalized) || tvLoosePattern.test(normalized);
  };

  const getMainCategoryLabel = (category) => (isTvCategory(category) ? "TVS" : category.name);

  const findBestTvCategory = (items) => {
    const scored = items
      .map((item) => {
        const normalized = normalizeCategoryName(item?.name);
        if (!normalized || tvExcludedPattern.test(normalized)) return null;
        if (/^tvs?$/.test(normalized)) return { item, score: 100 };
        if (/^smart\s*tvs?$/.test(normalized)) return { item, score: 95 };
        if (tvStrongPattern.test(normalized)) return { item, score: 90 };
        if (tvLoosePattern.test(normalized)) return { item, score: 80 };
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);

    return scored[0]?.item;
  };

  const resolveMainCategories = (items) => {
    const blockedMainPattern = /acessor|veicul|auto\s*peca/;
    const preferredResolvers = [
      () => items.find((item) => /celular|smartphone|iphone|android/.test(normalizeCategoryName(item?.name))),
      () => findBestTvCategory(items),
      () => items.find((item) => /informatica|notebook|computador|tablet/.test(normalizeCategoryName(item?.name))),
      () => items.find((item) => /movel|moveis|casa/.test(normalizeCategoryName(item?.name))),
      () => items.find((item) => /eletrodomestico|eletro|cozinha|geladeira|ar-condicionado/.test(normalizeCategoryName(item?.name))),
    ];

    const selected = [];
    const selectedIds = new Set();

    preferredResolvers.forEach((resolver) => {
      const found = resolver();

      if (found && !selectedIds.has(found.id)) {
        selected.push(found);
        selectedIds.add(found.id);
      }
    });

    items.forEach((item) => {
      if (selected.length >= 5) return;
      const normalized = normalizeCategoryName(item?.name);
      const isBlockedMain = normalized && blockedMainPattern.test(normalized);

      if (!selectedIds.has(item.id) && !isBlockedMain) {
        selected.push(item);
        selectedIds.add(item.id);
      }
    });

    return selected.slice(0, 5);
  };

  const buildDepartmentColumns = (items, totalColumns = 5) => {
    if (!items.length) return [];

    const columns = Array.from({ length: totalColumns }, () => []);
    items.forEach((item, index) => {
      columns[index % totalColumns].push(item);
    });

    return columns.filter((column) => column.length > 0);
  };

  const mainCategories = useMemo(() => resolveMainCategories(categories), [categories]);
  const departmentColumns = useMemo(() => buildDepartmentColumns(categories, 5), [categories]);
  const currentCategoryRoute = location.pathname.match(/^\/categoria\/([^/]+)/)?.[1] ?? null;
  const currentSubCategoryRoute = location.pathname.match(/^\/subCategory\/([^/]+)/)?.[1] ?? null;
  const preserveSearchSuffix = location.search || "";

  const handleDepartmentCategoryClick = (categoryId) => {
    history.push(`/categoria/${categoryId}${preserveSearchSuffix}`);
  };

  const handleDepartmentSubCategoryClick = (subCategoryId) => {
    history.push(`/subCategory/${subCategoryId}${preserveSearchSuffix}`);
  };

  const handleHeaderSearchSubmit = (event) => {
    event.preventDefault();
    const term = headerSearch.trim();
    const isMarketplacePath =
      location.pathname === "/" ||
      location.pathname.startsWith("/categoria/") ||
      location.pathname.startsWith("/subCategory/");
    const targetPath = isMarketplacePath ? location.pathname : "/";
    const params = new URLSearchParams(isMarketplacePath ? location.search : "");

    if (term) {
      params.set("q", term);
    } else {
      params.delete("q");
    }

    history.push(`${targetPath}${params.toString() ? `?${params.toString()}` : ""}`);
  };

  const renderUnlogged = () => (
    <Nav className="ml-auto">
      <Nav.Link as={Link} to="/entrar">
        Entrar
      </Nav.Link>
    </Nav>
  );

  const renderEntrepreneurSidebar = () => (
    <SubMenu title="Painel das Ofertas do Postador" defaultOpen>
      <MenuItem>
        <Link to="/poster/produtos" onClick={closeSidebar}>
          Meus Produtos
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/adicionar-oferta-ia" onClick={closeSidebar}>
          Nova Oferta com IA
        </Link>
      </MenuItem>
    </SubMenu>
  );

  const renderAdminSidebar = () => (
    <SubMenu title="Painel do Admin">
      <MenuItem>
        <Link to="/admin/dashboard" onClick={closeSidebar}>
          Dashboard
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/usuarios" onClick={closeSidebar}>
          Usuários
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/localidades" onClick={closeSidebar}>
          Localidades
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/categorias" onClick={closeSidebar}>
          Categorias
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/parceiros" onClick={closeSidebar}>
          Parceiros
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/tipos-categorias" onClick={closeSidebar}>
          Tipos de Categorias
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/evento" onClick={closeSidebar}>
          Eventos
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/products" onClick={closeSidebar}>
          Todos os Produtos
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/adicionar-oferta-ia" onClick={closeSidebar}>
          Nova Oferta com IA
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/banner" onClick={closeSidebar}>
          Todos os Banners
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/ribbon-badges" onClick={closeSidebar}>
          Todos os Emblemas
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/grupos" onClick={closeSidebar}>
          Controle de Acessos
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/controle/shoppe" onClick={closeSidebar}>
          Controle Shoppe
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/divulgacao/shoope" onClick={closeSidebar}>
          Divulgação
        </Link>
      </MenuItem>
      <MenuItem>
        <Link to="/admin/tema" onClick={closeSidebar}>
          Personalizar Tema
        </Link>
      </MenuItem>
    </SubMenu>
  );

  const renderUser = () => (
    <NavDropdown
      id="basic-nav-dropdown"
      title={
        <span className="align-self-center">
          <FaUserCircle className="text-secondary" size="22" />
          {width > 450 && <span className="ml-2">{user.name}</span>}
        </span>
      }
      alignRight
    >
      <NavDropdown.Item to="/conta" as={Link} onClick={closeSidebar}>
        Minha Conta
      </NavDropdown.Item>
      <NavDropdown.Item onClick={logout}>Sair</NavDropdown.Item>
    </NavDropdown>
  );

  const CategoryModal = ({ items, show, handleClose }) => {
    const resetSelection = () => {
      setLastCategory(null);
      setSelectedSubCategory(null);
      history.push("/");
      handleClose();
    };

    const handleCategorySelect = (id, isSubCategory, path) => {
      handleClose();
      history.push(path);

      if (isSubCategory) {
        setSelectedSubCategory(id);
        setLastCategory(null);
      } else {
        setLastCategory(id);
        setSelectedSubCategory(null);
      }
    };

    return (
      <Modal show={show} onHide={handleClose} size="lg" aria-labelledby="category-modal-title">
        <Modal.Header closeButton>
          <Modal.Title id="category-modal-title" style={{ cursor: "pointer" }} onClick={resetSelection}>
            Categorias
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            {items.map((category) => (
              <Col key={category.id} md={6} lg={4}>
                <div
                  className="category-section"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleCategorySelect(category.id, false, `/categoria/${category.id}`)}
                >
                  <h5>{category.name}</h5>
                  <ul>
                    {category.category_types?.map((subCat) => (
                      <li
                        key={subCat.id}
                        style={{ cursor: "pointer" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCategorySelect(subCat.id, true, `/subCategory/${subCat.id}`);
                        }}
                      >
                        {subCat.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
            ))}
          </Row>
        </Modal.Body>
      </Modal>
    );
  };

  const MobileNavbarWithDropdown = ({ items }) => {
    const [showModal, setShowModal] = useState(false);

    return (
      <MenuItem onClick={() => setShowModal(true)}>
        Categorias
        <CategoryModal items={items} show={showModal} handleClose={() => setShowModal(false)} />
      </MenuItem>
    );
  };

  return (
    <>
      <div className={`navbar-shell ${showMarketplaceCatalogBar ? "with-catalog-bar" : ""} ${isGruposRoute ? "on-grupos-page" : ""}`}>
        <Navbar fixed="top" bg="primary" variant="dark" className={showMarketplaceCatalogBar ? "navbar-main with-catalog" : "navbar-main"}>
          <Navbar.Brand as={Link} to="/" onClick={closeSidebar}>
            <img
              src={Logo}
              alt="Logo"
              width="128px"
              className="mx-2 d-flex align-self-center"
            />
          </Navbar.Brand>

          {width > 992 && (
            <Nav className="mr-auto">
              <Nav.Link as={Link} to="/grupos/" onClick={closeSidebar}>
                Grupo de Ofertas
              </Nav.Link>
              <Nav.Link as={Link} to="/sobre" onClick={closeSidebar}>
                Sobre
              </Nav.Link>
              {/* Pedir Produto desativado temporariamente - reativar quando ferramenta voltar
              <Nav.Link as={Link} to="/pedir-produto" onClick={closeSidebar}>
                Pedir Produto
              </Nav.Link>
              */}
              {user && user.type !== "client" && (
                <Nav.Link as={Link} to={user.type === "admin" ? "/admin" : "/poster"}>
                  Painel
                </Nav.Link>
              )}
              {user && user.type !== "client" && (
                <Nav.Link as={Link} to="/adicionar-oferta-ia" onClick={closeSidebar}>
                  Nova Oferta com IA
                </Nav.Link>
              )}
            </Nav>
          )}

          {width > 992 && (
            <Form className="header-search-form" onSubmit={handleHeaderSearchSubmit}>
              <Form.Control
                type="text"
                value={headerSearch}
                onChange={(event) => setHeaderSearch(event.target.value)}
                placeholder="O que está procurando?"
                aria-label="Pesquisar produtos"
                className="header-search-input"
              />
              <Button type="submit" variant="light" size="sm" className="header-search-button" aria-label="Buscar">
                Buscar
              </Button>
            </Form>
          )}

          {width < 992 && (
            <Button size="sm" className="ml-auto" onClick={() => setToggled((state) => !state)}>
              <GiHamburgerMenu />
            </Button>
          )}

          {user && <Nav xs="auto">{renderUser()}</Nav>}
          {!user && width > 992 && <Nav xs="auto">{renderUnlogged()}</Nav>}
        </Navbar>

        {showMarketplaceCatalogBar && (
          <div className="catalog-strip">
            <div className="catalog-strip-inner">
              <Nav className="catalog-nav">
                {mainCategories.map((category) => (
                  <Nav.Link
                    key={category.id}
                    onClick={() => handleDepartmentCategoryClick(category.id)}
                    className={`catalog-main-link ${String(currentCategoryRoute) === String(category.id) ? "active" : ""}`}
                  >
                    {getMainCategoryLabel(category)}
                  </Nav.Link>
                ))}

                <NavDropdown
                  alignRight
                  className="catalog-departments-dropdown"
                  title={
                    <span className="catalog-departments-title">
                      <FaBars />
                      Departamentos
                    </span>
                  }
                >
                  <div className="catalog-mega-menu">
                    {departmentColumns.map((column, columnIndex) => (
                      <div key={`department-column-${columnIndex}`} className="catalog-mega-column">
                        {column.map((category) => (
                          <div key={category.id} className="catalog-mega-category">
                            <button
                              type="button"
                              className={`catalog-mega-category-button ${String(currentCategoryRoute) === String(category.id) ? "active" : ""}`}
                              onClick={() => handleDepartmentCategoryClick(category.id)}
                            >
                              {category.name}
                            </button>

                            {category.category_types?.map((subCategory) => (
                              <button
                                key={subCategory.id}
                                type="button"
                                className={`catalog-mega-subcategory-button ${String(currentSubCategoryRoute) === String(subCategory.id) ? "active" : ""}`}
                                onClick={() => handleDepartmentSubCategoryClick(subCategory.id)}
                              >
                                {subCategory.name}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </NavDropdown>
              </Nav>
            </div>
          </div>
        )}

        {width < 992 && (
          <ProSidebar
            breakPoint="lg"
            toggled={toggled}
            onToggle={(value) => {
              setToggled(value);
            }}
          >
            <SidebarContent style={{ background: "var(--app-primary)" }}>
              <Menu>
                <MenuItem>
                  <Link to="/grupos/" onClick={closeSidebar}>
                    Grupo de Ofertas
                  </Link>
                </MenuItem>
                <MenuItem>
                  <Link to="/sobre" onClick={closeSidebar}>
                    Sobre
                  </Link>
                </MenuItem>
                <MenuItem>
                  <Link to="/" onClick={closeSidebar}>
                    Melhores Ofertas
                  </Link>
                </MenuItem>
                {/* Pedir Produto desativado temporariamente - reativar quando ferramenta voltar
                <MenuItem>
                  <Link to="/pedir-produto" onClick={closeSidebar}>
                    Pedir Produto
                  </Link>
                </MenuItem>
                */}
                <MobileNavbarWithDropdown items={categories} />
                {user && user.type !== "client" && (
                  <MenuItem>
                    <Link to="/adicionar-oferta-ia" onClick={closeSidebar}>
                      Nova Oferta com IA
                    </Link>
                  </MenuItem>
                )}
                {user && user.type === "poster" && renderEntrepreneurSidebar()}
                {user && user.type === "admin" && renderAdminSidebar()}
                {!user && (
                  <MenuItem>
                    <Link to="/entrar" onClick={closeSidebar}>
                      Entrar
                    </Link>
                  </MenuItem>
                )}
              </Menu>
            </SidebarContent>
          </ProSidebar>
        )}
      </div>
    </>
  );
}
